import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { useEffect, useMemo, useState } from 'react'
import { getAdminConfig, setAdminConfig } from 'services'
import { ToggleButton } from 'stories/components'

interface BorrowerFeatureData {
  title: string
  url: string
  order: number
  value: boolean
}

export const BorrowerQuickLinks = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [data, setData] = useState<Record<string, BorrowerFeatureData>>({})
  const [editingTitleKey, setEditingTitleKey] = useState<string | null>(null)

  const initData = async () => {
    setIsLoading(true)
    const borrowerFeaturesRes = await getAdminConfig('borrowerFeatures')
    setData(borrowerFeaturesRes)
    setIsLoading(false)
  }
  useEffect(() => {
    initData()
  }, [])

  const onChange = async (key: string, subKey: string, value: any) => {
    const temp = cloneDeep(data)
    temp[key] = { ...temp[key], [subKey]: value }
    setData(temp)
  }

  const onSubmit = async () => {
    setIsLoading(true)
    await setAdminConfig('borrowerFeatures', data)
    setIsLoading(false)
  }

  const handleTitleBlur = async () => {
    setEditingTitleKey(null)
    await onSubmit()
  }

  const orderOptions = useMemo(() => {
    const options: number[] = []
    Object.keys(data).forEach((_, index) => options.push(index + 1))
    return options
  }, [Object.keys(data).length])

  const changeOrder = async (key: string, newOrder: string) => {
    const temp = cloneDeep(data)
    const currentOrder = temp[key].order
    const targetOrder = parseInt(newOrder)

    Object.keys(temp).forEach((k) => {
      if (k === key) {
        temp[k].order = targetOrder
      } else if (currentOrder < targetOrder) {
        if (temp[k].order > currentOrder && temp[k].order <= targetOrder) {
          temp[k].order--
        }
      } else {
        if (temp[k].order >= targetOrder && temp[k].order < currentOrder) {
          temp[k].order++
        }
      }
    })

    setData(temp)
    setIsLoading(true)
    await setAdminConfig('borrowerFeatures', temp)
    setIsLoading(false)
  }

  const sortedData = useMemo(() => {
    return Object.entries(data).sort(([, a], [, b]) => a.order - b.order)
  }, [data])

  return (
    <>
      <LayoutLoading show={isLoading} />

      <div className="mt-4">
        {sortedData.map(([key, value], index: number) => {
        return (
          <div
            className={`flex justify-between items-center p-2 pl-4 ${index % 2 && 'bg-slate-100'}`}
            key={`${key}-${index}`}
          >
            <div className="flex-1 flex justify-between items-center">
              <div className="flex items-center gap-4">
                <span className="text-sm">{index + 1}.</span>
                {editingTitleKey === key ? (
                  <input
                    type="text"
                    className="text-sm border border-gray-300 rounded px-2 py-1 min-w-[200px] focus:outline-none focus:ring-2 focus:ring-shade-blue"
                    value={value.title}
                    onChange={(e) => onChange(key, 'title', e.target.value)}
                    onBlur={handleTitleBlur}
                    autoFocus
                    placeholder="Menu Title"
                  />
                ) : (
                  <span
                    className="text-sm cursor-pointer hover:underline min-w-[200px] px-2 py-1"
                    onClick={() => setEditingTitleKey(key)}
                  >
                    {value.title}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4">
                <select
                  className="py-1 border-gray-300 text-[15px]"
                  value={value.order.toString()}
                  onChange={(e) => changeOrder(key, e.target.value)}
                  onBlur={onSubmit}
                >
                  {orderOptions.map((item: any, index1) => {
                    return (
                      <option key={index1} value={`${item}`} disabled={item === index + 1}>
                        {item}
                      </option>
                    )
                  })}
                </select>
                <ToggleButton
                  id={`${key}-${index}`}
                  value={value.value}
                  onChange={(value) => onChange(key, 'value', value)}
                  onBlur={onSubmit}
                />
              </div>
            </div>
          </div>
        )
        })}
      </div>
    </>
  )
}
